import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import { useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useFieldArray, useForm } from 'react-hook-form'

import { Box, Chip } from '@mui/material'

import { showNotification } from 'utils/showNotification'

import { useSendTicketsMutation, useUploadTicketMutation } from 'services/bookings/api'
import { Sale, SendType } from 'services/bookings/types'

import { AppModal } from 'components/common/AppModal'
import Button from 'components/common/Button'
import CustomCheckbox from 'components/common/Checkbox'
import { CopyToClipboard } from 'components/common/CopyToClipboard'
import { CustomDropzone } from 'components/common/CustomDropzone'
import DatePicker from 'components/common/DatePicker'
import TextInput from 'components/common/TextInput'

import { SaveAndSendModal } from './SaveAndSendModal'
import { SEND_TICKETS_SCHEMA } from './validation'
import moment from 'moment'

export const SENT_STATUS_MAP = {
  0: { text: 'Not Sent', color: 'error' },
  10: { text: 'Partially Sent', color: 'secondary' },
  20: { text: 'Deferred', color: 'success' },
  30: { text: 'Sent', color: 'primary' },
} as const

interface Props {
  onClose: () => void
  sale: Sale
}

interface FormValues {
  tickets: {
    id: number
    linkedFile?: any
    holderFirstName?: string | null
    holderLastName?: string | null
    holderEmail: string
    holderBirthday?: string | null
    send: boolean
  }[]
  accountDetails: {
    email: string
    password: string
  }
  platformOrderId?: string | null
  send: boolean
  message?: string | null
  notes?: string | null
}

export const SendTicketsModal = ({ onClose, sale }: Props) => {
  const [sendTickets, { isLoading }] = useSendTicketsMutation()
  const [uploadTicket, { isLoading: isFilesLoading }] = useUploadTicketMutation()

  const [isSaveAndSendModalOpen, setSaveAndSendModalOpen] = useState(false)

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(SEND_TICKETS_SCHEMA),
    defaultValues: {
      accountDetails: {
        email: sale?.accountDetails?.email ?? '',
        password: sale?.accountDetails?.password ?? '',
      },
      platformOrderId: sale?.platformOrderId ?? '',
      send: sale?.sentStatus !== 10,
      tickets:
        sale?.tickets?.map((ticket) => ({
          id: ticket?.id,
          linkedFile: ticket?.linkedFile,
          holderFirstName: ticket?.holderFirstName ?? '',
          holderLastName: ticket?.holderLastName ?? '',
          holderEmail: ticket?.holderEmail ?? '',
          holderBirthday: ticket?.holderBirthday ?? '',
          send: ticket.sentStatusType !== 10,
        })) || [],
      message: '',
      notes: sale?.notes ?? '',
    },
  })

  const { fields: tickets } = useFieldArray({ control, name: 'tickets' })

  const onSubmit = (send: SendType) => async (formValues: FormValues) => {
    const filesMap = formValues.tickets
      .map((t) => t.linkedFile || [])
      .reduce(
        (prev, file, fileInd) => ({
          ...prev,
          [`${fileInd}`]: file?.length > 0 && file[0] instanceof File ? file[0] : null, //if file[0] and instanceof file === 'File'
        }),
        {}
      )

    const requests = []
    for (const file in filesMap) {
      if (filesMap[file] !== null) {
        const formData = new FormData()
        formData.append('tickets', filesMap[file])
        requests.push(uploadTicket(formData))
      }
    }
    const fileRequests = await Promise.all(requests)

    let requestsInd = 0
    for (const fileInd in filesMap) {
      if (filesMap[fileInd] !== null) {
        formValues.tickets[+fileInd].linkedFile = (
          fileRequests[requestsInd] as {
            data: {
              resultFilename: string
            }
          }
        )?.data?.resultFilename
        requestsInd += 1
      }
    }

    await sendTickets({
      ...formValues,
      id: sale.id,
      send: send,
      tickets: formValues.tickets.map((ticket) => ({
        ...ticket,
        holderBirthday: ticket.holderBirthday,
        linkedFile: ticket.linkedFile || null,
      })),
    })
      .unwrap()
      .then(() => {
        showNotification(`Tickets successfully ${send ? 'saved and sent' : 'saved'}`, 'success')
        onClose()
      })
      .catch(() => showNotification('Error. Try again later', 'error'))
  }

  const formatHolderBirthdayValue = (date: string | null | undefined) => {
    console.log(date)
    if (date) {
      let [year, month, day] = date.split('T')[0].split('-');
      return [day, month, year].join('-')
    }
    return ''
  }

  return (
    <>
      <AppModal
        onClose={onClose}
        title="Send tickets"
        maxWidth={800}
        loading={isLoading || isFilesLoading}
      >
        <AppModal.Wrapper
          component="form"
          noValidate
          sx={{ width: '800px', maxHeight: '80vh', height: 'fit-content' }}
          id="sendTicketsForm"
        >
          <AppModal.ContentBox sx={{ width: '800px' }}>
            <Box sx={{ display: 'flex', gap: 2, width: 1 }}>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <TextInput
                  label="Email"
                  control={control}
                  name="accountDetails.email"
                  errors={errors.accountDetails?.email?.message}
                />
                <CopyToClipboard
                  value={watch('accountDetails.email')}
                  field="email"
                  hideValue
                  wrapperSx={{ mt: 1.5 }}
                />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <TextInput
                  name="accountDetails.password"
                  label="Password"
                  errors={errors.accountDetails?.password?.message}
                  control={control}
                />
                <CopyToClipboard
                  value={watch('accountDetails.password')}
                  field="password"
                  hideValue
                  wrapperSx={{ mt: 1.5 }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <TextInput
                control={control}
                name="platformOrderId"
                label="Order ID"
                errors={errors}
              />
              <CopyToClipboard
                value={watch('platformOrderId') || ''}
                field="platformOrderId"
                hideValue
                wrapperSx={{ mt: 1.5 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              {tickets.map((ticket, ind) => (
                <Box
                  key={ticket.id}
                  sx={{
                    display: 'flex',
                    width: '100%',
                    flexDirection: 'column',
                    gap: 2,
                    borderBottom: '1px solid rgba(0,0,0,0.3)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <CustomCheckbox
                        control={control}
                        name={`tickets.${ind}.send`}
                        label={`Section: ${sale.tickets[ind].section}, row: ${sale.tickets[ind].row}, seat: ${sale.tickets[ind].seat}`}
                      />
                      <Chip
                        label={
                          (sale?.tickets?.[ind]?.sentStatusType ||
                            sale?.tickets?.[ind]?.sentStatusType === 0) &&
                          SENT_STATUS_MAP[sale?.tickets?.[ind]?.sentStatusType].text
                        }
                        color={
                          ((sale?.tickets?.[ind]?.sentStatusType ||
                            sale?.tickets?.[ind]?.sentStatusType === 0) &&
                            SENT_STATUS_MAP[sale?.tickets?.[ind]?.sentStatusType].color) ||
                          'info'
                        }
                      />
                    </Box>
                    <CustomDropzone
                      setValue={setValue}
                      control={control}
                      name={`tickets.${ind}.linkedFile`}
                      noMediaTitle={'Attach file'}
                      maxSize={2 * 2 ** 20}
                      formatsAsLabel=".pdf"
                      acceptedFormats={{
                        'application/pdf': ['.pdf'],
                        'image/jpeg': ['.jpg', '.jpeg'],
                        'image/png': ['.png'],
                      }}
                      viewType="button"
                      error={errors.tickets?.[ind]?.linkedFile?.message as string}
                      isPdf={false}
                    />
                  </Box>
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <TextInput
                          control={control}
                          name={`tickets.${ind}.holderEmail`}
                          label="Holder Email"
                          errors={errors?.tickets?.[ind]?.holderEmail?.message}
                        />
                        <CopyToClipboard
                          value={watch(`tickets.${ind}.holderEmail`)}
                          field="holderEmail"
                          hideValue
                          wrapperSx={{ mt: 1.5 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <DatePicker
                          inputFormat="dd-MM-yyyy"
                          control={control}
                          name={`tickets.${ind}.holderBirthday`}
                          label="Holder Birthday"
                          errors={errors?.tickets?.[ind]?.holderBirthday?.message}
                        />
                        <CopyToClipboard
                          value={formatHolderBirthdayValue(watch(`tickets.${ind}.holderBirthday`))}
                          field="holderBirthday"
                          hideValue
                          wrapperSx={{ mt: 1.5 }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <TextInput
                          control={control}
                          name={`tickets.${ind}.holderFirstName`}
                          label="Holder First Name"
                          errors={errors?.tickets?.[ind]?.holderFirstName?.message}
                        />
                        <CopyToClipboard
                          value={watch(`tickets.${ind}.holderFirstName`) || ''}
                          field="holderFirstName"
                          hideValue
                          wrapperSx={{ mt: 1.5 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <TextInput
                          control={control}
                          name={`tickets.${ind}.holderLastName`}
                          label="Holder Last Name"
                          errors={errors?.tickets?.[ind]?.holderLastName?.message}
                        />
                        <CopyToClipboard
                          value={watch(`tickets.${ind}.holderLastName`) || ''}
                          field="holderLastName"
                          hideValue
                          wrapperSx={{ mt: 1.5 }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextInput
                control={control}
                name="notes"
                label="Notes"
                multiline
                rows={3}
                errors={errors?.notes?.message}
              />
            </Box>
          </AppModal.ContentBox>
          <AppModal.ButtonsBox>
            <Button variant="outlined" color="error" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              fullWidth={isMobileOnly}
              onClick={handleSubmit(onSubmit(SendType.DONT_SEND))}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="contained"
              disabled={isLoading}
              fullWidth={isMobileOnly}
              onClick={() => setSaveAndSendModalOpen(true)}
            >
              Save & Send
            </Button>
          </AppModal.ButtonsBox>
          {isSaveAndSendModalOpen && (
            <SaveAndSendModal
              onClose={() => setSaveAndSendModalOpen(false)}
              isLoading={isLoading}
              control={control}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
            />
          )}
        </AppModal.Wrapper>
      </AppModal>
    </>
  )
}
